import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Plus,
  Search,
} from 'lucide-react'
import {
  buildStaffPaginationPages,
  fetchStaffDirectory,
  formatStaffDate,
  formatStaffPaginationSummary,
  mapStaffMemberFromApi,
  STAFF_DEPARTMENTS,
  STAFF_EMPLOYMENT_TYPES,
  STAFF_STATUSES,
} from '../../../services/staffDirectoryService'
import { getAdminAuthToken } from '../../../services/adminAuthService'
import StaffStatusBadge from './StaffStatusBadge'
import StaffFormModal from './StaffFormModal'
import StaffActionsModal from './StaffActionsModal'

const STATUS_TABS = [{ value: 'all', label: 'All' }, ...STAFF_STATUSES]

export default function StaffDirectoryPage() {
  const [activeTab, setActiveTab] = useState('all')
  const [search, setSearch] = useState('')
  const [department, setDepartment] = useState('all')
  const [employmentType, setEmploymentType] = useState('all')
  const [page, setPage] = useState(1)
  const [staff, setStaff] = useState([])
  const [options, setOptions] = useState([])
  const [pagination, setPagination] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [formState, setFormState] = useState(null) // { member } | { member: null }
  const [actionModal, setActionModal] = useState(null) // { action, member }
  const [menuOpenId, setMenuOpenId] = useState(null)
  const [menuPos, setMenuPos] = useState(null)
  const menuTriggerRefs = useRef({})

  const loadStaff = useCallback(async () => {
    if (!getAdminAuthToken()) {
      setError('Please sign in to view the staff directory.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const result = await fetchStaffDirectory({ search, department, status: activeTab, employmentType, page, perPage: 15 })
      setStaff(result.staff.map(mapStaffMemberFromApi))
      setPagination(result.pagination)
    } catch (err) {
      setStaff([])
      setPagination(null)
      setError(err.response?.data?.message || 'Unable to load the staff directory.')
    } finally {
      setLoading(false)
    }
  }, [activeTab, department, employmentType, page, search])

  const loadOptions = useCallback(async () => {
    try {
      const result = await fetchStaffDirectory({ perPage: 100 })
      setOptions(result.staff.map((m) => ({ id: m.id, name: m.name, employeeId: m.employee_id })))
    } catch {
      setOptions([])
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(loadStaff, search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [loadStaff])

  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  const refresh = useCallback(() => {
    loadStaff()
    loadOptions()
  }, [loadStaff, loadOptions])

  const paginationMeta = useMemo(() => {
    if (!pagination) {
      return { pages: [1], activePage: 1, lastPage: 1, showingRange: '0 to 0', showingSuffix: '' }
    }
    const summary = formatStaffPaginationSummary(pagination)
    return {
      pages: buildStaffPaginationPages(pagination),
      activePage: pagination.current_page,
      lastPage: pagination.last_page,
      showingRange: summary.showingRange,
      showingSuffix: summary.showingSuffix,
    }
  }, [pagination])

  const closeMenu = () => {
    setMenuOpenId(null)
    setMenuPos(null)
  }

  const toggleMenu = (id) => {
    if (menuOpenId === id) {
      closeMenu()
      return
    }
    const button = menuTriggerRefs.current[id]
    const rect = button?.getBoundingClientRect()
    setMenuPos(rect ? { top: rect.bottom + 6, right: window.innerWidth - rect.right } : null)
    setMenuOpenId(id)
  }

  const openAction = (action, member) => {
    closeMenu()
    setActionModal({ action, member })
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col p-6">
      <header className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Staff Directory</h1>
          <p className="text-xs text-gray-500">Internal company employees (no platform login).</p>
        </div>
        <button
          type="button"
          onClick={() => setFormState({ member: null })}
          className="inline-flex items-center gap-2 rounded-lg bg-rapid-orange px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E84A15]"
        >
          <Plus size={16} strokeWidth={2} aria-hidden />
          Add staff member
        </button>
      </header>

      <div className="mt-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full max-w-md">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search name, email, ID or job title…"
              className="w-full rounded-lg bg-gray-100 py-2 pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-rapid-orange/30"
            />
          </div>
          <div className="relative">
            <select
              value={department}
              onChange={(e) => { setDepartment(e.target.value); setPage(1) }}
              className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-rapid-orange/30"
            >
              <option value="all">All departments</option>
              {STAFF_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
          </div>
          <div className="relative">
            <select
              value={employmentType}
              onChange={(e) => { setEmploymentType(e.target.value); setPage(1) }}
              className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-rapid-orange/30"
            >
              <option value="all">All types</option>
              {STAFF_EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
          </div>
        </div>

        <nav className="flex flex-wrap gap-6 border-b border-gray-200" aria-label="Staff status filters">
          {STATUS_TABS.map((tab) => {
            const isActive = activeTab === tab.value
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => { setActiveTab(tab.value); setPage(1) }}
                className={`-mb-px border-b-2 pb-2 text-sm font-semibold transition-colors ${
                  isActive ? 'border-rapid-orange text-rapid-orange' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {error ? (
        <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="mt-4 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Staff member</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Department</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Job title</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Hired</th>
                <th className="w-12 px-6 py-3" aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-500">Loading staff…</td></tr>
              ) : staff.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-500">No staff match this filter.</td></tr>
              ) : (
                staff.map((member) => (
                  <tr key={member.id} className="border-b border-gray-50 transition-colors hover:bg-gray-50/40 last:border-0">
                    <td className="px-6 py-4">
                      <Link to={`/admin/staff/${member.id}`} className="flex items-center gap-3 transition-opacity hover:opacity-90">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-rapid-orange">
                          {member.initials}
                        </span>
                        <span>
                          <span className="block text-sm font-bold text-gray-900 hover:text-rapid-orange">{member.name}</span>
                          <span className="block text-xs text-gray-500">{member.employeeId}{member.email ? ` · ${member.email}` : ''}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{member.department || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{member.jobTitle || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{member.employmentTypeLabel}</td>
                    <td className="px-6 py-4"><StaffStatusBadge status={member.status} label={member.statusLabel} /></td>
                    <td className="px-6 py-4 text-xs text-gray-500">{formatStaffDate(member.hireDate)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative inline-block">
                        <button
                          type="button"
                          ref={(el) => { menuTriggerRefs.current[member.id] = el }}
                          onClick={() => toggleMenu(member.id)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                          aria-label={`Actions for ${member.name}`}
                          aria-haspopup="menu"
                          aria-expanded={menuOpenId === member.id}
                        >
                          <MoreVertical size={18} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-gray-500">
          Showing <span className="font-semibold text-gray-700">{paginationMeta.showingRange}</span> {paginationMeta.showingSuffix}
        </p>
        <nav className="flex items-center gap-1.5" aria-label="Pagination">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-40"
            aria-label="Previous page"
            disabled={loading || page <= 1}
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </button>
          {paginationMeta.pages.map((pageNumber, idx) =>
            pageNumber === '…' ? (
              <span key={`ellipsis-${idx}`} className="px-1 text-xs text-gray-400">…</span>
            ) : (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                className={`flex h-7 min-w-[1.75rem] items-center justify-center rounded-md px-2 text-xs font-semibold transition-colors ${
                  pageNumber === paginationMeta.activePage ? 'bg-rapid-orange text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-current={pageNumber === paginationMeta.activePage ? 'page' : undefined}
              >
                {pageNumber}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => setPage((p) => (paginationMeta.lastPage ? Math.min(paginationMeta.lastPage, p + 1) : p + 1))}
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-40"
            aria-label="Next page"
            disabled={loading || !paginationMeta.lastPage || page >= paginationMeta.lastPage}
          >
            <ChevronRight size={14} strokeWidth={2} />
          </button>
        </nav>
      </footer>

      <StaffFormModal
        isOpen={Boolean(formState)}
        onClose={() => setFormState(null)}
        member={formState?.member}
        staffOptions={options}
        onSaved={refresh}
      />
      <StaffActionsModal
        isOpen={Boolean(actionModal)}
        onClose={() => setActionModal(null)}
        action={actionModal?.action}
        member={actionModal?.member}
        onSuccess={refresh}
      />

      {menuOpenId && menuPos
        ? createPortal(
            <div style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 50 }}>
              <button type="button" aria-label="Close menu" className="fixed inset-0 z-40 cursor-default" onClick={closeMenu} />
              <div className="relative z-50 w-48 rounded-xl border border-gray-100 bg-white py-1 shadow-xl">
                {staff.filter((m) => m.id === menuOpenId).map((member) => (
                  <RowMenu key={member.id} member={member} openAction={openAction} />
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

function RowMenu({ member, openAction }) {
  return (
    <>
      <MenuItem as={Link} to={`/admin/staff/${member.id}`}>View profile</MenuItem>
      <MenuItem onClick={() => openAction('edit', member)}>Edit details</MenuItem>
      <MenuItem onClick={() => openAction('status', member)}>Change status</MenuItem>
      <div className="my-1 border-t border-gray-100" />
      <MenuItem onClick={() => openAction('delete', member)} tone="danger">Remove staff member</MenuItem>
    </>
  )
}

function MenuItem({ children, onClick, tone = 'default', as: Component = 'button', ...rest }) {
  const className = `block w-full px-4 py-2 text-left text-sm transition-colors ${
    tone === 'danger' ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
  }`
  if (Component !== 'button') {
    return <Component className={className} {...rest}>{children}</Component>
  }
  return (
    <button type="button" onClick={onClick} className={className} {...rest}>
      {children}
    </button>
  )
}


